import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, Lock, Wrench, Save, Loader2, CheckCircle2, X } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'public', label: 'Public', icon: Globe, desc: 'Live and visible to everyone', color: 'bg-emerald-500' },
  { value: 'private', label: 'Private', icon: Lock, desc: 'Only signed-in users can view', color: 'bg-amber-500' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, desc: 'Public visitors see a maintenance page', color: 'bg-[#c8102e]' },
];

const SECTION_TOGGLES = [
  { key: 'section_team_gallery', label: 'Team Gallery' },
  { key: 'section_facebook_feed', label: 'Facebook Feed' },
  { key: 'section_experience', label: 'Experience Timeline' },
  { key: 'section_stats', label: 'Stats Section' },
  { key: 'section_iso', label: 'ISO Certification' },
  { key: 'section_industries', label: 'Industries We Serve' },
  { key: 'section_insurance_cta', label: 'Insurance CTA' },
  { key: 'section_testimonials', label: 'Testimonials' },
  { key: 'section_faq', label: 'FAQ' },
  { key: 'section_partners', label: 'Partners Section' },
];

const Card = ({ title, subtitle, children }) => (
  <div className="bg-white border border-neutral-200 p-6 md:p-8 mb-6">
    <div className="mb-6">
      <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">{title}</span></div>
      {subtitle && <p className="text-sm text-neutral-500">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">{label}</label>
    {children}
    {hint && <p className="text-xs text-neutral-400 mt-1.5">{hint}</p>}
  </div>
);

const SiteSettingsPage = () => {
  const { token } = useAuth();
  const { settings, refresh } = useSiteSettings();
  const { toast } = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings && Object.keys(settings).length) setForm(settings); }, [settings]);

  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async (patch = null) => {
    setSaving(true);
    try {
      const body = patch || form;
      const res = await axios.patch(`${API}/admin/settings`, body, { headers: { Authorization: `Bearer ${token}` } });
      setForm(res.data);
      await refresh();
      toast({ title: 'Saved', description: 'Site settings have been updated.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally { setSaving(false); }
  };

  const setStatus = (value) => save({ site_status: value });

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Site Settings</span></div>
        <h1 className="font-serif-display text-4xl font-bold text-black">Customize your website</h1>
      </div>

      <Card title="Site Status" subtitle="Control who can see your website">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {STATUS_OPTIONS.map((s) => {
            const active = form.site_status === s.value;
            const Icon = s.icon;
            return (
              <button key={s.value} type="button" onClick={() => setStatus(s.value)} disabled={saving} className={`text-left p-5 border-2 transition-all ${active ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}>
                <div className={`w-10 h-10 flex items-center justify-center mb-3 ${s.color}`}><Icon className="w-4 h-4 text-white" /></div>
                <div className="font-serif-display text-lg font-bold text-black flex items-center gap-2">{s.label} {active && <CheckCircle2 className="w-4 h-4 text-[#c8102e]" />}</div>
                <div className="text-xs text-neutral-500 mt-1">{s.desc}</div>
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-6 pt-6 border-t border-neutral-100">
          <div>
            <div className="font-semibold text-black text-sm">Allow new signups</div>
            <div className="text-xs text-neutral-500">If off, no new users can create accounts.</div>
          </div>
          <Switch checked={!!form.signup_enabled} onCheckedChange={(v) => { set('signup_enabled', v); save({ signup_enabled: v }); }} />
        </div>
      </Card>

      <Card title="Announcement Bar" subtitle="Show a strip at the top of every page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-semibold text-black text-sm">Show announcement bar</div>
            <div className="text-xs text-neutral-500">Appears above the header on every page.</div>
          </div>
          <Switch checked={!!form.announcement_enabled} onCheckedChange={(v) => set('announcement_enabled', v)} />
        </div>
        <Field label="Announcement text"><Input value={form.announcement_text || ''} onChange={(e) => set('announcement_text', e.target.value)} placeholder="Free pickup this week across Milan!" className="h-12" /></Field>
        <Field label="Optional link" hint="External link opens in a new tab. Leave blank for none."><Input value={form.announcement_link || ''} onChange={(e) => set('announcement_link', e.target.value)} placeholder="/contact or https://..." className="h-12" /></Field>
        <Button onClick={() => save()} disabled={saving} className="bg-[#c8102e] hover:bg-black text-white h-11 px-6 text-[12px] tracking-[0.15em] uppercase font-semibold">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Announcement
        </Button>
      </Card>

      <Card title="Hero Section" subtitle="Override the homepage hero text. Leave blank to use defaults.">
        <Field label="Eyebrow (small red label)"><Input value={form.hero_eyebrow || ''} onChange={(e) => set('hero_eyebrow', e.target.value)} placeholder="Premium Global Logistics • Est. 2014" className="h-12" /></Field>
        <Field label="Title line 1"><Input value={form.hero_title_line1 || ''} onChange={(e) => set('hero_title_line1', e.target.value)} placeholder="We deliver happiness," className="h-12" /></Field>
        <Field label="Title line 2"><Input value={form.hero_title_line2 || ''} onChange={(e) => set('hero_title_line2', e.target.value)} placeholder="one parcel at a time." className="h-12" /></Field>
        <Field label="Subtitle paragraph"><Textarea value={form.hero_subtitle || ''} onChange={(e) => set('hero_subtitle', e.target.value)} placeholder="Eleven years of moving what matters most..." rows={3} /></Field>
        <Button onClick={() => save()} disabled={saving} className="bg-[#c8102e] hover:bg-black text-white h-11 px-6 text-[12px] tracking-[0.15em] uppercase font-semibold">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Hero
        </Button>
      </Card>

      <Card title="Contact Information" subtitle="Override site-wide phone, email and address. Leave blank to use defaults.">
        <Field label="Phone"><Input value={form.company_phone || ''} onChange={(e) => set('company_phone', e.target.value)} placeholder="+39 327 989 0838" className="h-12" /></Field>
        <Field label="Email"><Input value={form.company_email || ''} onChange={(e) => set('company_email', e.target.value)} placeholder="contact@lankaexpresslogi.com" className="h-12" /></Field>
        <Field label="Head office address"><Textarea value={form.company_address || ''} onChange={(e) => set('company_address', e.target.value)} rows={2} /></Field>
        <Button onClick={() => save()} disabled={saving} className="bg-[#c8102e] hover:bg-black text-white h-11 px-6 text-[12px] tracking-[0.15em] uppercase font-semibold">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Contact
        </Button>
      </Card>

      <Card title="Section Visibility" subtitle="Turn homepage sections on or off">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          {SECTION_TOGGLES.map((t) => (
            <div key={t.key} className="flex items-center justify-between py-2 border-b border-neutral-100">
              <span className="text-sm font-medium text-black">{t.label}</span>
              <Switch checked={form[t.key] !== false} onCheckedChange={(v) => { set(t.key, v); save({ [t.key]: v }); }} />
            </div>
          ))}
        </div>
      </Card>
    </AdminLayout>
  );
};

export default SiteSettingsPage;
